import { useContext, useEffect, useMemo, useState } from "react";

import useFetchData from "../hooks/useFetchData";
import MetricCategoriesContext from "../context/MetricCategoriesContext";
import LoadingIndicator from "../components/LoadingIndicator";
import { categorizeData, orgTabMap } from "../utils/dashboardUtils";
import { useOrgId } from "../context/OrgIdContext";
import OrganizationMetrics from "../components/OrganizationMetrics";
import MetadataContext from "../context/MetadataContext";
import LayerSummary from "../components/LayerSummary";
import LayersContext from "../context/LayersContext";

const Dashboard = () => {
    const orgId = useOrgId();
    const isIframe = window.self !== window.top;
    
    // Fetch metric categories from context
    const { metricCategories, loading: categoriesLoading } = useContext(MetricCategoriesContext);
    const { metadata } = useContext(MetadataContext);
    const { layers } = useContext(LayersContext);

    // State to track the currently selected category
    const [currentTab, setCurrentTab] = useState<string>("");

    // Fetch data based on the current category
    const { indicators, loading } = useFetchData(currentTab || '');

    // Set the default tab when metric categories are loaded or orgId changes
    useEffect(() => {
        // Ensure orgId exists
        if (orgId) {
            const defaultTab = orgTabMap[orgId] || metricCategories[0];
            setCurrentTab(defaultTab);
        }
    }, [metricCategories, orgId]);

    // Ensure data is available before rendering
    const allDataAvailable = useMemo(() => !loading && indicators, [loading, indicators]);

    // Render metrics for the selected tab
    const renderMetrics = (tab: string) => {
        // Show loading indicator if data is still being fetched
        if (loading || categoriesLoading) {
            return <LoadingIndicator />;
        }

        if (!metricCategories.includes(tab)) {
            if (layers?.length > 0) {
                return <LayerSummary dashboardView={tab} />;
            } else {
                return (
                    <div className="flex items-center justify-center text-color-cai-dashboard">
                        Loading layer data...
                    </div>
                );
            }
        }

        // Handle case where no metrics are available for the selected category
        if (!indicators || indicators.length === 0) {
            return (
                <div className="flex items-center justify-center text-color-cai-dashboard">
                    No metrics available for {tab}.
                </div>
            );
        }

        const cateogorizedData = categorizeData(orgId, indicators);

        return (
            <>
                {/* Render metric cards */}
                <OrganizationMetrics orgId={orgId} indicators={indicators} metadata={metadata} tab={currentTab} categorizedData={cateogorizedData} geoDetails={[]} geoTag={null}/>
            </>
        );
    };

    return (
        <div className={`${!isIframe ? "max-w-5xl mx-auto print:max-w-full" : ""}`}>
            {/* Render metrics for the selected category */}
            <div className="no-print">
                {allDataAvailable ? (
                    renderMetrics(currentTab)
                ) : (
                    <div className="loading-container">
                        <div className="loading-spinner"></div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Dashboard;
